/**
 * ProfilerPanel Component
 *
 * Main drawer panel that displays all profiling visualizations including
 * timing breakdown, pie chart, waterfall timeline, and historical trends.
 */

import React, {useMemo, useState} from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Chip,
    Divider,
    Drawer,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    List,
    ListItemButton,
    ListItemText,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    Switch,
    Tab,
    Tabs,
    Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TimelineIcon from '@mui/icons-material/Timeline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {useProfiler} from '../../context/profilerContext';
import {ProfileSession} from '../../@types/profiler';
import TimingBarChart from './TimingBarChart';
import TimingPieChart from './TimingPieChart';
import WaterfallChart from './WaterfallChart';
import TrendChart from './TrendChart';

interface GroupedSessions {
    ftName: string;
    totalTime: number;
    timestamp: Date;
    groupId: string;
    sessions: { session: ProfileSession; index: number; actionName: string }[];
}

/**
 * Extract FT name from a session's contextLabel.
 * Handles formats like:
 * - "createFtGraphAndExpand: Engine Fire (450 nodes)" -> "Engine Fire"
 * - "Deferred decorations: Engine Fire" -> "Engine Fire"
 * - "toDendogram: Source Name (123 elements)" -> "Source Name"
 * - "toggleBranch (nodeName)" -> "toggleBranch"
 */
function extractFtName(contextLabel: string): string {
    // Check for "action: Name" format
    const colonIndex = contextLabel.indexOf(':');
    if (colonIndex !== -1) {
        let name = contextLabel.substring(colonIndex + 1).trim();
        // Remove trailing parenthesis content like "(450 nodes)"
        const parenIndex = name.lastIndexOf('(');
        if (parenIndex > 0) {
            name = name.substring(0, parenIndex).trim();
        }
        return name || 'Unknown';
    }
    // Fallback: return the action name before parenthesis
    const parenIndex = contextLabel.indexOf('(');
    if (parenIndex > 0) {
        return contextLabel.substring(0, parenIndex).trim();
    }
    return contextLabel;
}

/**
 * Extract action name from a session's contextLabel.
 * Returns the part before the colon, or the full label if no colon.
 */
function extractActionName(contextLabel: string): string {
    const colonIndex = contextLabel.indexOf(':');
    if (colonIndex !== -1) {
        return contextLabel.substring(0, colonIndex).trim();
    }
    return contextLabel;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
    return (
        <Box
            role="tabpanel"
            hidden={value !== index}
            sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}
        >
            {value === index && children}
        </Box>
    );
};

const DRAWER_WIDTH = 500;

const ProfilerPanel: React.FC = () => {
    const profilerContext = useProfiler();
    const {
        isEnabled = false,
        isPanelOpen = false,
        currentSession = null,
        history = [],
        activeEsdName = null,
        toggle = (): void => {},
        closePanel = (): void => {},
        clearHistory = (): void => {}
    } = profilerContext || {};

    const [tabValue, setTabValue] = useState(0);
    const [selectedSessionIndex, setSelectedSessionIndex] = useState<number>(0);
    const [expandedFt, setExpandedFt] = useState<string | false>(false);
    const [ftFilter, setFtFilter] = useState<string>(''); // Empty string means show most recent FT

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number): void => {
        setTabValue(newValue);
    };

    const handleAccordionChange = (ftName: string) => (_event: React.SyntheticEvent, isExpanded: boolean): void => {
        setExpandedFt(isExpanded ? ftName : false);
    };

    const handleSessionSelect = (index: number): void => {
        setSelectedSessionIndex(index);
    };

    const handleFtFilterChange = (event: SelectChangeEvent<string>): void => {
        setFtFilter(event.target.value);
    };

    // Get unique FT names from history
    const uniqueFtNames = useMemo((): string[] => {
        const names = new Set<string>();
        history.forEach((session) => {
            names.add(extractFtName(session.contextLabel));
        });
        return Array.from(names);
    }, [history]);

    // Determine active filter (prioritize: manual selection > active ESD from tab > most recent session)
    const activeFtFilter = useMemo((): string => {
        if (ftFilter === '__ALL__') return '';
        if (ftFilter) return ftFilter;
        // Use active ESD name from canvas tab if available
        if (activeEsdName && uniqueFtNames.includes(activeEsdName)) {
            return activeEsdName;
        }
        if (history.length > 0) {
            return extractFtName(history[0].contextLabel);
        }
        return '';
    }, [ftFilter, activeEsdName, uniqueFtNames, history]);

    // Group sessions by FT name and expansion event (sessions with same FT within time window)
    const groupedSessions = useMemo((): GroupedSessions[] => {
        const groups: GroupedSessions[] = [];
        const TIME_THRESHOLD_MS = 10000; // Sessions within 10 seconds are part of same expansion

        // Process history (newest first)
        history.forEach((session, index) => {
            const ftName = extractFtName(session.contextLabel);
            const actionName = extractActionName(session.contextLabel);
            const sessionTime = new Date(session.timestamp).getTime();

            // Find an existing group with same FT name and within time threshold
            const matchingGroup = groups.find(group => {
                if (group.ftName !== ftName) return false;
                // Check if this session is within threshold of any session in the group
                return group.sessions.some(({ session: s }) => {
                    const sTime = new Date(s.timestamp).getTime();
                    return Math.abs(sessionTime - sTime) < TIME_THRESHOLD_MS;
                });
            });

            if (matchingGroup) {
                matchingGroup.totalTime += session.totalTime;
                matchingGroup.sessions.push({ session, index, actionName });
            } else {
                // Start a new group
                groups.push({
                    ftName,
                    totalTime: session.totalTime,
                    timestamp: session.timestamp,
                    groupId: `${ftName}-${session.id}`,
                    sessions: [{ session, index, actionName }]
                });
            }
        });

        return groups;
    }, [history]);

    // Filter groups by active FT filter
    const filteredGroups = useMemo((): GroupedSessions[] => {
        if (!activeFtFilter) return groupedSessions;
        return groupedSessions.filter(group => group.ftName === activeFtFilter);
    }, [groupedSessions, activeFtFilter]);

    // Get the session to display (current or selected from history)
    const displaySession = history[selectedSessionIndex] || currentSession;

    return (
        <Drawer
            anchor="right"
            open={isPanelOpen}
            onClose={closePanel}
            sx={{
                '& .MuiDrawer-paper': {
                    width: DRAWER_WIDTH,
                    boxSizing: 'border-box',
                }
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText'
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <TimelineIcon />
                    <Typography variant="h6" component="div">
                        Performance Profiler
                    </Typography>
                </Stack>
                <IconButton
                    onClick={closePanel}
                    sx={{ color: 'inherit' }}
                    size="small"
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Controls */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={isEnabled}
                                onChange={toggle}
                                color="primary"
                            />
                        }
                        label={
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="body2">Profiling</Typography>
                                <Chip
                                    label={isEnabled ? 'ON' : 'OFF'}
                                    size="small"
                                    color={isEnabled ? 'success' : 'default'}
                                />
                            </Stack>
                        }
                    />
                    <Button
                        startIcon={<DeleteOutlineIcon />}
                        onClick={clearHistory}
                        size="small"
                        disabled={history.length === 0}
                    >
                        Clear History
                    </Button>
                </Stack>

                {/* ESD Filter */}
                {uniqueFtNames.length > 0 && (
                    <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                        <InputLabel>Filter by Safety View</InputLabel>
                        <Select
                            value={ftFilter || activeFtFilter || '__ALL__'}
                            label="Filter by Safety View"
                            onChange={handleFtFilterChange}
                        >
                            <MenuItem value="__ALL__">
                                <em>Show All</em>
                            </MenuItem>
                            {uniqueFtNames.map((name) => (
                                <MenuItem key={name} value={name}>
                                    {name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </Box>

            {/* Grouped Session Selector */}
            {filteredGroups.length > 0 && (
                <Box sx={{ maxHeight: 250, overflow: 'auto', borderBottom: 1, borderColor: 'divider' }}>
                    {filteredGroups.map((group) => (
                        <Accordion
                            key={group.groupId}
                            expanded={expandedFt === group.groupId}
                            onChange={handleAccordionChange(group.groupId)}
                            disableGutters
                            sx={{
                                '&:before': { display: 'none' },
                                boxShadow: 'none'
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    minHeight: 44,
                                    '& .MuiAccordionSummary-content': { my: 0.5 }
                                }}
                            >
                                <Stack direction="column" sx={{ width: '100%', pr: 1 }}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>
                                            {group.ftName}
                                        </Typography>
                                        <Chip
                                            label={`${group.totalTime.toFixed(0)}ms`}
                                            size="small"
                                            color="primary"
                                        />
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary">
                                        {new Date(group.timestamp).toLocaleTimeString()}
                                    </Typography>
                                </Stack>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0 }}>
                                <List dense disablePadding>
                                    {group.sessions.map(({ session, index, actionName }) => (
                                        <ListItemButton
                                            key={session.id}
                                            selected={selectedSessionIndex === index}
                                            onClick={(): void => handleSessionSelect(index)}
                                            sx={{ py: 0.5, pl: 3 }}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Typography variant="body2">
                                                            {actionName}
                                                        </Typography>
                                                        <Chip
                                                            label={`${session.totalTime.toFixed(0)}ms`}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
                                                        />
                                                    </Stack>
                                                }
                                            />
                                        </ListItemButton>
                                    ))}
                                </List>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            )}

            {/* Selected Session Info */}
            {displaySession && (
                <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50' }}>
                    <Typography variant="caption" color="text.secondary">
                        Selected: {extractActionName(displaySession.contextLabel)}
                    </Typography>
                </Box>
            )}

            <Divider />

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    variant="fullWidth"
                >
                    <Tab label="Breakdown" />
                    <Tab label="Timeline" />
                    <Tab label="Trend" />
                </Tabs>
            </Box>

            {/* Tab Panels */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                {/* Breakdown Tab */}
                <TabPanel value={tabValue} index={0}>
                    {displaySession ? (
                        <Box sx={{ p: 1 }}>
                            <TimingBarChart session={displaySession} height={280} />
                            <Divider sx={{ my: 2 }} />
                            <TimingPieChart session={displaySession} height={220} />
                        </Box>
                    ) : (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary">
                                No profiling data yet.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Enable profiling and perform an action (like expanding a fault tree)
                                to see timing data.
                            </Typography>
                        </Box>
                    )}
                </TabPanel>

                {/* Timeline Tab */}
                <TabPanel value={tabValue} index={1}>
                    {displaySession ? (
                        <WaterfallChart session={displaySession} />
                    ) : (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary">
                                No profiling data yet.
                            </Typography>
                        </Box>
                    )}
                </TabPanel>

                {/* Trend Tab */}
                <TabPanel value={tabValue} index={2}>
                    <Box sx={{ p: 1 }}>
                        <TrendChart sessions={history} height={300} />
                        {history.length > 0 && (
                            <Box sx={{ mt: 2, px: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Showing last {history.length} session(s)
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </TabPanel>
            </Box>
        </Drawer>
    );
};

export default ProfilerPanel;
